import DataQuery from 'data/DataQuery';
import IFileService from 'data/IFileService';
import { useQuery } from 'data/hooks/useQuery';
import { CommodityDetail } from 'features/contracts/enums/commodityDetail';
import MeasuredDataResultDTO from 'features/shared/dtos/MeasuredDataResultDTO';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import YearSelect from '../selectInput/yearSelect/YearSelect';
import MonthSelect from '../selectInput/monthSelect/MonthSelect';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DropdownActionItem from '../dropdown/DropdownActionItem';
import NoContentWrapper from '../noContentWrapper/NoContentWrapper';
import MeasuredDataGraphCard from '../graphs/MeasuredDataGraphCard';
import { getActualOrEndYear, getMonthBoundaries, getMeasuredDataParams } from './measuredDataHelper';
import MeasuredDataHeader from './MeasuredDataHeader';

type Props = {
  title: string;
  identificationParams: URLSearchParams;
  graphDataUrl: string;
  graphDataParams?: URLSearchParams;
  downloadDataUrl: string;
  downloadDataParams?: URLSearchParams;
  commodityDetail: CommodityDetail;
  from: Date;
  to: Date;
  year?: number;
  isProduction?: boolean;
}

const MeasuredData = ({ 
  title,
  identificationParams,
  graphDataUrl,
  graphDataParams,
  downloadDataUrl,
  downloadDataParams,
  commodityDetail,
  from,
  to,
  year, 
  isProduction
 }: Props) => {
  //#region translations
  const tLabels = useTranslation("shared\\components").t;  
  //#endregion translations

  //#region DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);
  //#endregion DI

  //#region state 
  // state init values
  const startYear = getActualOrEndYear(to);
  const boundaries = getMonthBoundaries(from, to, startYear);

  const [selectedYear, setSelectedYear] = useState<number>(year ?? startYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(boundaries[0]);
  const [monthBoundaries, setMonthBoundaries] = useState<number[]>(boundaries);
  //#endregion state  

  //#region fetch
  const measuredDataQuery: DataQuery = {
    url: graphDataUrl,
    params: getMeasuredDataParams(identificationParams, selectedYear, selectedMonth, isProduction, graphDataParams)
  };
  const { data: realValues, loadStatus: realValuesStatus } =
    useQuery<MeasuredDataResultDTO>(measuredDataQuery, {}, [
      selectedYear,
      selectedMonth,
    ]);
  //#endregion fetch

  //#endregion side-effects
  useEffect(() => {
    if (year) {
      yearChangeHandler(year);
    }
  }, [year])
  
  //#endregion side-effects

  //#region helpers
  const downloadMeasuredDataExcel = (selectedYear: number, selectedMonth?: number) => { 
    let query: DataQuery = {
      url: downloadDataUrl,
      params: getMeasuredDataParams(identificationParams, selectedYear, selectedMonth, isProduction, undefined, downloadDataParams)
    };
    
    fileService.downloadFile(query);
   }
  //#endregion helpers

  //#region handlers
  const yearChangeHandler = (year: number) => {
    let actualYearBoundaries = getMonthBoundaries(from, to, year);
    
    setSelectedYear(year);
    setMonthBoundaries(actualYearBoundaries);
    setSelectedMonth(actualYearBoundaries[0]);
  }

  const downloadMonthDataExcelHandler = () => {
    downloadMeasuredDataExcel(selectedYear, selectedMonth);
  }

  const downloadYearDataExcelHandler = () => {
    downloadMeasuredDataExcel(selectedYear);
  }
  //#endregion handlers

  //#region sub-renders
  const renderYearSelect = () => {
    return (
      <YearSelect 
        startDate={from}
        endDate={to}
        selectedYear={selectedYear}
        className="w-100"
        onChange={(e) => yearChangeHandler(Number(e.target.value))}
      />
    );
  }
  
  const renderMonthSelect = () => {
    return (
      <MonthSelect 
        startMonth={monthBoundaries[0]}
        endMonth={monthBoundaries[1]}
        selectedMonth={selectedMonth}
        className="w-100"
        onChange={(e) => setSelectedMonth(Number(e.target.value))}
      />
    );
  }

  const renderDownloadExcelBtn = () => {
    if (realValues.measuredValues && realValues.measuredValues.length > 0) {
      return (
        <Dropdown>
          <Dropdown.Toggle id="profile-data-dropdown" size='sm' variant='secondary' className='mt-3 mt-sm-0' block>
            <span className="mr-2">
              <FontAwesomeIcon icon="file-excel" />
            </span>
            <span className="mr-1">
              {tLabels("measuredData.downloadExcel")}
            </span>
          </Dropdown.Toggle>

          <Dropdown.Menu >
            <DropdownActionItem 
              title={tLabels("measuredData.selectedMonth")} 
              icon="download" 
              onClick={downloadMonthDataExcelHandler}
            />
            <DropdownActionItem
              title={tLabels("measuredData.selectedYear")} 
              icon="download"
              onClick={downloadYearDataExcelHandler}
            />
          </Dropdown.Menu>
        </Dropdown>
      );
    }
  }
  //#endregion sub-renders

  //#region render
  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between mt-4 mb-3">
        <h4 className="mr-3 mb-0 text-muted">{title}</h4>
      </div>
      <MeasuredDataHeader 
        renderYearSelect={renderYearSelect}
        renderMonthSelect={renderMonthSelect}
        renderDownloadExcelBtn={renderDownloadExcelBtn}
      />
      <NoContentWrapper
        data={realValues.measuredValues}
        loadStatus={realValuesStatus}
        noContentAsAlert
        noContentInfo={isProduction ? tLabels("measuredData.noProductionDataInfo") : undefined}
      >
        <MeasuredDataGraphCard 
          commodityDetail={commodityDetail}
          realValues={realValues}
          isProduction={isProduction}
        />
      </NoContentWrapper>
    </>
    //#endregion render
  )
}

export default MeasuredData;